import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient from '@/api/httpClient.ts';

export interface DemoDataStatus {
    has_demo_data: boolean;
    statistics: {
        customers: number;
        trading_accounts: number;
        orders: number;
        positions: number;
        trades: number;
        trade_history: number;
        transactions: number;
        leads: number;
        tasks: number;
        audit_logs: number;
        customer_documents: number;
        customer_addresses: number;
        kyc_requests: number;
        kyc_documents: number;
        payment_methods: number;
        exchange_rates: number;
        settings: number;
        assets: number;
        instruments: number;
        notification_templates: number;
        notification_rules: number;
        notification_triggers: number;
    };
}

export interface DemoDataStats {
    statistics: {
        customers: number;
        trading_accounts: number;
        orders: number;
        positions: number;
        trades: number;
        trade_history: number;
        transactions: number;
        leads: number;
        tasks: number;
        audit_logs: number;
        customer_documents: number;
        customer_addresses: number;
        kyc_requests: number;
        kyc_documents: number;
        payment_methods: number;
        exchange_rates: number;
        settings: number;
        assets: number;
        instruments: number;
        notification_templates: number;
        notification_rules: number;
        notification_triggers: number;
    };
    timestamp: string;
}

export interface DemoDataDeletionResult {
    deletion_stats: {
        customers: number;
        trading_accounts: number;
        orders: number;
        positions: number;
        trades: number;
        trade_history: number;
        transactions: number;
        leads: number;
        tasks: number;
        audit_logs: number;
        customer_documents: number;
        customer_addresses: number;
        kyc_requests: number;
        kyc_documents: number;
        payment_methods: number;
        exchange_rates: number;
        settings: number;
    };
    before_stats: DemoDataStats['statistics'];
    after_stats: DemoDataStats['statistics'];
    deleted_at: string;
}

export interface DemoDataCreationResult {
    success: boolean;
    message: string;
    statistics: DemoDataStats['statistics'];
    created_at: string;
}

// Check demo data status
export const useDemoDataStatus = () => {
    return useQuery({
        queryKey: ['demo-data', 'status'],
        queryFn: async (): Promise<DemoDataStatus> => {
            const response = await httpClient.get('/crm/demo-data/status');
            return response.data.data;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

// Get demo data statistics
export const useDemoDataStats = () => {
    return useQuery({
        queryKey: ['demo-data', 'stats'],
        queryFn: async (): Promise<DemoDataStats> => {
            const response = await httpClient.get('/crm/demo-data/stats');
            return response.data.data;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

// Delete demo data
export const useDeleteDemoData = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (): Promise<DemoDataDeletionResult> => {
            const response = await httpClient.delete('/crm/demo-data');
            return response.data.data;
        },
        onSuccess: () => {
            // Invalidate related queries
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            queryClient.invalidateQueries({ queryKey: ['leads'] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
            queryClient.invalidateQueries({ queryKey: ['kyc-requests'] });
            queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
            queryClient.invalidateQueries({ queryKey: ['settings'] });
            queryClient.invalidateQueries({ queryKey: ['demo-data'] });

            // Refetch dashboard stats
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        },
    });
};

// Create demo data
export const useCreateDemoData = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (): Promise<DemoDataCreationResult> => {
            const response = await httpClient.post('/crm/demo-data');
            return response.data.data;
        },
        onSuccess: () => {
            // Invalidate related queries
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            queryClient.invalidateQueries({ queryKey: ['leads'] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
            queryClient.invalidateQueries({ queryKey: ['kyc-requests'] });
            queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
            queryClient.invalidateQueries({ queryKey: ['settings'] });
            queryClient.invalidateQueries({ queryKey: ['demo-data'] });

            // Refetch dashboard stats
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        },
    });
};
